import { useEffect, useRef } from 'react';
import { CopilotBridge, CopilotContextOwner, CopilotPanelContentBase, CopilotPanelContext } from './copilotBridge';

/**
 * Publishes one scene's Copilot support while it is mounted.
 *
 * `content` must be referentially stable (memoize it): a new instance re-activates the context, which
 * resets the panel conversation and notifies every consumer of the bridge state.
 */
export const useCopilotContext = (
  bridge: CopilotBridge,
  context: CopilotPanelContext,
  content: CopilotPanelContentBase,
  options?: { enabled?: boolean; forceOpen?: boolean; forcePinned?: boolean },
) => {
  const ownerRef = useRef<CopilotContextOwner>(Symbol('CopilotContextOwner'));
  const owner = ownerRef.current;
  const enabled = options?.enabled ?? true;

  useEffect(() => {
    if (!enabled) {
      bridge.releaseContext(owner);

      return;
    }

    if (options?.forceOpen) {
      bridge.isOpen = true;
    }
    bridge.activateContext(owner, context, content);
  }, [bridge, context, content, enabled, options?.forceOpen, owner]);

  useEffect(() => {
    if (!enabled || options?.forcePinned === undefined) return;

    bridge.forcePinned(options.forcePinned);

    return () => bridge.forcePinned(undefined);
  }, [bridge, enabled, options?.forcePinned]);

  useEffect(() => {
    return () => {
      bridge.releaseContext(owner);
    };
  }, [bridge, owner]);
};
